import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Breadcrumb from "../../components/Breadcrumb";
import { api } from "../../utils/apiClient";
import LoadingShimmer from "../../utils/LoadingShimmer";
import GlobalFilter from "../../components/filters/GlobalFilter";
import { showDeleteConfirm } from "../../utils/deleteConfirm";
import { toast } from "react-toastify";
import { Icon } from "@iconify/react/dist/iconify.js";
import ReadingPlanTable from "../../components/tables/ReadingPlanTable";
import ReadingPlanViewModal from "../../components/modal/ReadingPlanViewModal";

let searchTimeout;

const ReadingPlan = () => {
    const [readingPlan, setReadingPlan] = useState([]);
    const [loading, setLoading] = useState(true);

    // pagination
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalEntries, setTotalEntries] = useState(0);
    // sort
    const [sort, setSort] = useState("created_at");
    const [order, setOrder] = useState("desc");
    // search
    const [search, setSearch] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");
    const [dateFilters, setDateFilters] = useState({ startDate: "", endDate: "" });
    const [selectedDates, setSelectedDates] = useState([]);
    const [mobileFilterOpen, setMobileFilterOpen] = useState(false);
    const [viewModalOpen, setViewModalOpen] = useState(false);
    const [selectedReadingPlan, setSelectedReadingPlan] = useState(null);
    const [entriesPerPage, setEntriesPerPage] = useState(10);
    const [activeTab, setActiveTab] = useState("all");
    const [langFilter, setLangFilter] = useState("all");
    const [planTypeFilter, setPlanTypeFilter] = useState("");

    const handleView = (plan) => {
        setSelectedReadingPlan(plan);
        setViewModalOpen(true);
    };

    useEffect(() => {
        clearTimeout(searchTimeout);
        searchTimeout = setTimeout(() => {
            setDebouncedSearch(search);
            setPage(1);
        }, 1000);
        return () => clearTimeout(searchTimeout);
    }, [search]);

    const TAB_RANGES = {
        all:   { minDays: null, maxDays: null },
        "25":  { minDays: 10,  maxDays: 25 },
        "50":  { minDays: 26,  maxDays: 50 },
        "150": { minDays: 51,  maxDays: 150 },
        "200": { minDays: 151, maxDays: 200 },
        "300": { minDays: 201, maxDays: 363 },
    };

    const fetchReadingPlan = async () => {
        try {
            setLoading(true);

            if (activeTab === "completed") {
                const response = await api.get("/readingplan/completed", {
                    params: {
                        page,
                        limit: entriesPerPage,
                        order,
                        search: debouncedSearch?.trim() || "",
                    },
                });
                setReadingPlan(response.data.data || []);
                setTotalPages(response.data.pagination.totalPages || 1);
                setTotalEntries(response.data.pagination.total || 0);
                return;
            }

            const { minDays, maxDays } = TAB_RANGES[activeTab] || {};
            const response = await api.get("/readingplan", {
                params: {
                    page,
                    limit: entriesPerPage,
                    sort,
                    order,
                    search: debouncedSearch?.trim() || "",
                    startDate: dateFilters.startDate || undefined,
                    endDate: dateFilters.endDate || undefined,
                    minDays: minDays ?? undefined,
                    maxDays: maxDays ?? undefined,
                    app_name: langFilter !== "all" ? langFilter : undefined,
                    plan_type: planTypeFilter || undefined,
                },
            });

            setReadingPlan(response.data.data || []);
            setTotalPages(response.data.pagination.totalPages || 1);
            setTotalEntries(response.data.pagination.total || 0);
        } catch (error) {
            console.error("Error fetching readingPlan:", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchReadingPlan();
    }, [page, entriesPerPage, sort, order, debouncedSearch, dateFilters, activeTab, langFilter, planTypeFilter]);

    const handleClearFilters = () => {
        setDateFilters({ startDate: "", endDate: "" });
        setSelectedDates([]);
        setPage(1);
    };

    const formatLocalDate = (date) => {
        if (!date) return "";
        const year = date.getFullYear();
        const month = (date.getMonth() + 1).toString().padStart(2, "0");
        const day = date.getDate().toString().padStart(2, "0");
        return `${year}-${month}-${day}`;
    };

    const handleDateFilter = ({ startDate, endDate, rawDates }) => {
        setSelectedDates(rawDates);
        setDateFilters({
            startDate: formatLocalDate(startDate),
            endDate: formatLocalDate(endDate),
        });
        setPage(1);
    };

    return (
        <>
            <div className="page-header-row">
                <Breadcrumb title="Reading Plan Management" />
                <div className="filter-wrapper">
                    <div className="d-flex align-items-center gap-3">
                        <Icon
                            icon="mdi:refresh"
                            width="26"
                            height="26"
                            className={`ms-2 d-inline-flex d-md-none mobile-refresh-icon ${loading ? "spin" : ""}`}
                            onClick={fetchReadingPlan}
                        />
                        <div className="filter-btn-wrapper">
                            <button
                                className="filter-btn"
                                onClick={() => setMobileFilterOpen(!mobileFilterOpen)}
                            >
                                <img src="assets\images\filter.png" alt="" className="filter-icon" />
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <div className={`filter-panel ${mobileFilterOpen ? "open" : ""}`}>
                <GlobalFilter
                    showDateRange={true}
                    onSearch={handleDateFilter}
                    onClear={handleClearFilters}
                    selectedDates={selectedDates}
                    showHundredUser={false}
                />
            </div>

            {loading ? (
                <LoadingShimmer
                    columns={8}
                    rows={6}
                    avatarColumnIndex={2}
                    actionColumnIndex={7}
                />
            ) : (
                <ReadingPlanTable
                    readingplan={readingPlan}
                    page={page}
                    setPage={setPage}
                    totalPages={totalPages}
                    totalEntries={totalEntries}
                    sort={sort}
                    order={order}
                    setSort={setSort}
                    setOrder={setOrder}
                    search={search}
                    setSearch={setSearch}
                    onView={handleView}
                    fetchReadingPlan={fetchReadingPlan}
                    entriesPerPage={entriesPerPage}
                    setEntriesPerPage={setEntriesPerPage}
                    activeTab={activeTab}
                    setActiveTab={setActiveTab}
                    langFilter={langFilter}
                    setLangFilter={setLangFilter}
                    planTypeFilter={planTypeFilter}
                    setPlanTypeFilter={setPlanTypeFilter}
                />
            )}

            <ReadingPlanViewModal
                open={viewModalOpen}
                onClose={() => setViewModalOpen(false)}
                data={selectedReadingPlan}
            />
        </>
    );
};

export default ReadingPlan;
